import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Droplets } from "lucide-react";
import { scrollToId } from "@/lib/apiClient";

const links = [
  { label: "Ministers", href: "#ministers" },
  { label: "The Ecosystem", href: "#engines" },
  { label: "Messages", href: "#message" },
  { label: "Live Session", href: "/live" },
  { label: "Come to the Water", href: "#connect" },
  { label: "Give", href: "#give" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) navigate(href);
    else scrollToId(href);
  };

  return (
    <motion.nav
      data-testid="main-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
        scrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} data-testid="nav-logo" className="flex items-center gap-3 group">
          <Droplets className="w-6 h-6 text-gold group-hover:text-blue transition-colors duration-300" strokeWidth={1.4} />
          <span className="font-display text-2xl tracking-wide text-white">
            F<span className="text-gold">.</span>O<span className="text-gold">.</span>L<span className="text-gold">.</span>W<span className="text-gold">.</span>A<span className="text-gold">.</span>C<span className="text-gold">.</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="text-sm font-light text-[#B4ADC1] hover:text-gold tracking-wide transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("#connect")}
            className="px-6 py-2.5 rounded-full text-sm font-medium text-black bg-gold hover:bg-[#e6c14e] transition-colors duration-300"
          >
            Request Prayer
          </button>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left text-[#B4ADC1] hover:text-gold py-1">
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#connect")} className="mt-2 px-6 py-3 rounded-full text-sm font-medium text-black bg-gold">
                Request Prayer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
